import { Button, Icon, RadioButton } from '@aqarmap/aqarmap-ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '~/components/Modal';
import { useBreakpoint } from '~/hooks';
import { closeModal, openModal } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch } from '~/redux/hooks';
import { SavedPropertyFilterType } from '~/types/activitiesTypes';
import sortData from '~activities/pages/Favorites/sort.json';

type SortData = {
  id: number;
  title_ar: string;
  title: string;
  slug: string;
  direction: string;
};

const Sort = ({
  filterState,
  setFilterState,
}: {
  filterState: SavedPropertyFilterType;
  setFilterState: React.Dispatch<React.SetStateAction<SavedPropertyFilterType>>;
}) => {
  const { mobileScreen } = useBreakpoint();
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const dispatch = useAppDispatch();
  const ref = React.useRef<HTMLDivElement>(null);

  const currentSortItem =
    sortData.data.filter(
      (item) =>
        item.slug === filterState.sort &&
        item.direction === filterState.direction,
    )[0] || sortData.data[0];

  const [selectedSort, setSelectedSort] = useState<SortData>(currentSortItem);

  const isDefaultSort = filterState.sort === '' && filterState.direction === '';
  const sortDisplayText = isDefaultSort
    ? t('sortBy')
    : locale === 'ar'
      ? selectedSort.title_ar
      : selectedSort.title;

  const buttonColorsClasses = `
  ${!isDefaultSort ? ' text-blue' : ' text-gray__dark_1'}`;

  const handleSortChange = (item: SortData) => {
    setSelectedSort(item);
    setFilterState((prev) => ({
      ...prev,
      sort: item.slug,
      direction: item.direction,
    }));
    setIsSortMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return mobileScreen ? (
    <>
      <button
        className={`${
          buttonColorsClasses
        } rounded-full h-[48px] bg-gray_light_1 md:rounded-[10px] py-x px-1.5x flex justify-center items-center gap-x z-30 md:hidden w-max`}
        onClick={() => dispatch(openModal('sortModal'))}
      >
        <Icon
          name="ascending"
          className={isDefaultSort ? 'text-gray__dark_2' : 'text-blue'}
        />
        <p
          className={`${isDefaultSort ? 'text-gray__dark_2' : 'text-blue'} text-body_2 hidden md:block`}
        >
          {sortDisplayText}
        </p>
      </button>
      <Modal
        modalKey="sortModal"
        title={t('sortBy')}
        onClose={() => setSelectedSort(currentSortItem)}
      >
        <div className="flex flex-col gap-3x w-full pb-4x">
          <div className="flex flex-col gap-1.5x">
            {sortData.data.map((item) => (
              <RadioButton
                key={item.id}
                label={{
                  text: locale === 'ar' ? item.title_ar : item.title,
                  className: 'text-body_2',
                }}
                field={{
                  checked:
                    item.slug === selectedSort.slug &&
                    item.direction === selectedSort.direction,
                  onChange: () => setSelectedSort(item),
                  value: item.slug,
                  id: `sort-${item.id}`,
                }}
                size="medium"
              />
            ))}
          </div>
          <Button
            variant="primary"
            size="medium"
            onClick={() => {
              setFilterState((prev) => ({
                ...prev,
                sort: selectedSort.slug,
                direction: selectedSort.direction,
              }));
              dispatch(closeModal('sortModal'));
            }}
            className="w-full btn-primary element-center gap-0.5x rounded-[26px] text-white h-[40px]"
          >
            <p className="text-link">{t('save')}</p>
          </Button>
        </div>
      </Modal>
    </>
  ) : (
    <div ref={ref} className="relative self-start">
      <button
        type="button"
        onClick={() => setIsSortMenuOpen((currentValue) => !currentValue)}
        className={`${isDefaultSort ? 'text-gray__dark_2' : 'text-blue'} flex w-[190px] h-[44px] items-center justify-between gap-1.5x rounded-[12px] border border-gray bg-white p-x text-body-2`}
      >
        <div className="flex items-center gap-x">
          <Icon name="ascending" />
          <span>{sortDisplayText}</span>
        </div>
        <Icon
          name="chevron-down"
          className={isSortMenuOpen ? 'rotate-180' : ''}
        />
      </button>

      {isSortMenuOpen ? (
        <div className="absolute end-0 top-[56px] z-[60] w-full rounded-[12px] border border-gray bg-white p-x shadow-sm">
          {sortData.data.map((option) => (
            <button
              key={option.id}
              type="button"
              className={[
                'flex w-full items-center justify-between px-1.5x py-x text-start text-body-2 hover:text-blue transition-colors',
                selectedSort.id === option.id
                  ? 'text-blue'
                  : 'text-gray__dark_2',
              ].join(' ')}
              onClick={() => handleSortChange(option)}
            >
              <span>{locale === 'ar' ? option.title_ar : option.title}</span>
            </button>
          ))}

          <div
            style={{
              left: '32px',
            }}
            className="sm:start-2x  absolute top-[-8px] transform  w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-gray"
          ></div>
        </div>
      ) : null}
    </div>
  );
};

export default Sort;
